import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Link } from '@/components/Link'
import { Spinner } from '@/components/Spinner'
import {
  DateCell,
  TextCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHost,
  TableRow,
} from '@/components/Table'
import { TableScroller } from '@/components/Table/TableScroller'
import { formatMoney } from '@/utils/money-util'
import styles from './ClientDetails.module.css'
import { links } from '@/utils/link'
import { IconButton } from '@/components/IconButton'

import type { Id } from '@/infra/id'
import { isSupported, copy } from 'clipbrd'

export interface MembershipRow {
  key: string
  workspaceName: string
  isWaitlistWorkspace: boolean
  lastSeen: Date | null
  canCreateJob: boolean
  onCreateJob: () => unknown
}

export interface WaitlistRow {
  key: string
  workspaceName: string
  loading: boolean
  error: string | null
  isInviteDisabled: boolean
  onInvite: () => unknown
}

export interface ClientDetailsProps {
  fullName: string
  email: string
  phone: string
  membershipEntries: MembershipRow[]
  waitlistEntries: WaitlistRow[]
  shouldShowEmptyMembership: boolean
  onAddToWorkspace: () => void
  jobHistory: ReactNode
}

export function ClientDetails({
  fullName,
  email,
  phone,
  membershipEntries,
  waitlistEntries,
  shouldShowEmptyMembership,
  onAddToWorkspace,
  jobHistory,
}: ClientDetailsProps) {
  return (
    <div className={styles.root}>
      <ContactCard fullName={fullName} email={email} phone={phone} />
      <MembershipCard
        membershipEntries={membershipEntries}
        waitlistEntries={waitlistEntries}
        shouldShowEmptyMembership={shouldShowEmptyMembership}
        onAddToWorkspace={onAddToWorkspace}
      />
      {jobHistory}
    </div>
  )
}

export interface ContactCardProps {
  fullName: string
  email: string
  phone: string
  workspace?: string
}

export function ContactCard({
  fullName,
  email,
  phone,
  workspace,
}: ContactCardProps) {
  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionCardTitle}>Contact</div>
      <div className={styles.contactGrid}>
        <ContactField label="Name" value={fullName || '—'} />
        <ContactField label="Email" value={email || '—'} />
        <ContactField label="Phone" value={phone || '—'} />
        {workspace && <ContactField label="Workspace" value={workspace} />}
      </div>
    </div>
  )
}

interface MembershipCardProps {
  membershipEntries: MembershipRow[]
  waitlistEntries: WaitlistRow[]
  shouldShowEmptyMembership: boolean
  onAddToWorkspace: () => void
}

function MembershipCard({
  membershipEntries,
  waitlistEntries,
  shouldShowEmptyMembership,
  onAddToWorkspace,
}: MembershipCardProps) {
  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionCardTitle}>Workspace memberships</div>
      {shouldShowEmptyMembership ? (
        <div className={styles.jobsEmpty}>No workspace memberships found.</div>
      ) : (
        <div className={styles.membershipsTableContainer}>
          <TableHost>
            <TableScroller>
              <Table striped>
                <TableHead>
                  <TableHeader alignment="left">Workspace</TableHeader>
                  <TableHeader alignment="left">Last login</TableHeader>
                  <TableHeader alignment="right">Actions</TableHeader>
                </TableHead>
                <TableBody>
                  {membershipEntries.map((item) => (
                    <MembershipItem key={item.key} item={item} />
                  ))}
                  {waitlistEntries.map((item) => (
                    <WaitlistItem key={item.key} item={item} />
                  ))}
                </TableBody>
              </Table>
            </TableScroller>
          </TableHost>
        </div>
      )}
      <div className={styles.membershipCardFooter}>
        <Button
          label="Add to workspace"
          variant="primary"
          size="normal"
          onPress={onAddToWorkspace}
        />
      </div>
    </div>
  )
}

export interface JobHistoryRow {
  id: Id
  shortId: string
  name: string
  workspaceName: string
  statusText: string
  dateCreated: Date
  specs: string
  total?: number
  navigable: boolean
}

export interface JobHistoryCardProps {
  failed: boolean
  isEmpty: boolean
  jobs: JobHistoryRow[]
  loader?: ReactNode
}

export function JobHistoryCard({
  failed,
  isEmpty,
  jobs,
  loader,
}: JobHistoryCardProps) {
  const copyTextToClipboard = (text: string) => {
    if (isSupported()) {
      copy(text)
    }
  }

  if (failed) {
    return (
      <div className={styles.sectionCard}>
        <div className={styles.sectionCardTitle}>Job history</div>
        <div className={styles.jobsError}>
          Unable to load job history. Please try again.
        </div>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className={styles.sectionCard}>
        <div className={styles.sectionCardTitle}>Job history</div>
        <div className={styles.jobsEmpty}>No jobs found for this client.</div>
      </div>
    )
  }

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionCardTitle}>Job history</div>
      <div className={styles.jobsTableContainer}>
        <TableHost>
          <TableScroller>
            <Table striped>
              <TableHead>
                <TableHeader alignment="left">Actions</TableHeader>
                <TableHeader alignment="left">Job ID</TableHeader>
                <TableHeader alignment="left">Name</TableHeader>
                <TableHeader alignment="left">Workspace</TableHeader>
                <TableHeader alignment="left">Status</TableHeader>
                <TableHeader alignment="left">Date created</TableHeader>
                <TableHeader alignment="left">Specs</TableHeader>
                <TableHeader alignment="right">Total</TableHeader>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TextCell alignment="left">
                      {job.navigable && job.id && (
                        <Link to={links.callCenterJob(job.id)}>
                          <Button
                            label="Go to Job"
                            variant="primary"
                            size="small"
                          />
                        </Link>
                      )}
                    </TextCell>
                    <TextCell>
                      <div className={styles.jobIdCell}>
                        {job.shortId}

                        {isSupported() && (
                          <IconButton
                            name="copy"
                            label="Copy job ID"
                            variant="ghost"
                            className={styles.copy}
                            onPress={() => copyTextToClipboard(job.shortId)}
                            inline
                            size={16}
                          />
                        )}
                      </div>
                    </TextCell>
                    <TextCell>{job.name}</TextCell>
                    <TextCell>{job.workspaceName}</TextCell>
                    <TextCell>{job.statusText}</TextCell>
                    <DateCell date={job.dateCreated} />
                    <TextCell>{job.specs}</TextCell>
                    <TextCell alignment="right">
                      {job.total !== undefined ? formatMoney(job.total) : '—'}
                    </TextCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loader}
          </TableScroller>
        </TableHost>
      </div>
    </div>
  )
}

interface ContactFieldProps {
  label: string
  value: ReactNode
}

function ContactField({ label, value }: ContactFieldProps) {
  return (
    <div>
      <div className={styles.contactFieldLabel}>{label}</div>
      <div className={styles.contactFieldValue}>{value || '—'}</div>
    </div>
  )
}

interface MembershipItemProps {
  item: MembershipRow
}

function MembershipItem({ item }: MembershipItemProps) {
  return (
    <TableRow>
      <TableCell>
        <div className={styles.workspaceCell}>
          {item.workspaceName}
          {item.isWaitlistWorkspace && (
            <span className={styles.memberBadge}>Signed up for waitlist</span>
          )}
        </div>
      </TableCell>
      <DateCell date={item.lastSeen} />
      <TableCell>
        <div className={styles.actionCell}>
          <Button
            label="Create job"
            variant="default"
            size="small"
            isDisabled={!item.canCreateJob}
            onPress={item.onCreateJob}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

interface WaitlistItemProps {
  item: WaitlistRow
}

function WaitlistItem({ item }: WaitlistItemProps) {
  const description = item.loading
    ? 'Processing...'
    : item.error
      ? item.error
      : 'This contact signed up for the waitlist in this workspace.'

  return (
    <TableRow>
      <TableCell>
        <div className={styles.workspaceCell}>
          <div>
            <div>{item.workspaceName}</div>
            <div className={styles.waitlistDescription}>
              {item.loading && (
                <span className={styles.waitlistLoading}>
                  <Spinner size={16} />
                </span>
              )}
              {'* ' + description}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell />
      <TableCell>
        <div className={styles.actionCell}>
          <Button
            label="Add to workspace"
            variant="default"
            size="small"
            onPress={item.onInvite}
            isDisabled={item.isInviteDisabled}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}
